import { useEffect, useMemo, useState } from "react";
import { gql, useApolloClient, useMutation } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Bell,
  FileArchive,
  FolderKanban,
  LayoutDashboard,
  MessageCircle,
  Settings,
  Shield,
  Users,
} from "lucide-react";
import type {
  MenuDef,
  MenuItemDef,
  MenuLabelDef,
  MenuName,
  SidebarProps,
  SidebarUser,
} from "../types/sidebar.types";
import { ROUTE_ROLES, hasAnyRole, normRoles } from "@/routes/routeAccess";

/* ========= GraphQL ========= */
const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout {
      message
    }
  }
`;

/* ========= Helpers ========= */
const getStoredUser = (): SidebarUser => {
  const saved = localStorage.getItem("userData");

  if (!saved) {
    return { firstName: "User", lastName: "", roles: [] };
  }

  try {
    const parsed = JSON.parse(saved);

    if (parsed?.profile) {
      return {
        firstName: parsed.profile.firstName ?? "User",
        lastName: parsed.profile.lastName ?? "",
        roles: normRoles(parsed?.roles),
      };
    }

    return {
      firstName: "User",
      lastName: "",
      roles: normRoles(parsed?.roles),
    };
  } catch {
    return { firstName: "User", lastName: "", roles: [] };
  }
};

const getStoredCollapsed = () => {
  return localStorage.getItem("sidebarCollapsed") === "1";
};

const getMatchedItemByPath = (items: MenuItemDef[], pathname: string) => {
  /*
   * Project detail pages use /projects/:id for all roles.
   *
   * CLIENT users do not have the "Projects" menu item.
   * They access project details from "My Projects".
   *
   * So for /projects/:id:
   * 1. Prefer My Projects if it is visible.
   * 2. Otherwise use Projects.
   */
  if (pathname.startsWith("/projects/")) {
    const myProjectsItem = items.find(
      (item) => item.name === "My Projects",
    );

    if (myProjectsItem) {
      return myProjectsItem;
    }

    const projectsItem = items.find(
      (item) => item.name === "Projects",
    );

    if (projectsItem) {
      return projectsItem;
    }
  }

  return items.find(
    (item) =>
      item.path &&
      (pathname === item.path || pathname.startsWith(`${item.path}/`)),
  );
};

const getInitialInspectionEditState = () => {
  try {
    const flag = (window as Window & { __INSPECTION_IS_EDITING?: boolean })
      .__INSPECTION_IS_EDITING;

    return typeof flag === "boolean" ? flag : false;
  } catch {
    return false;
  }
};

/* ========= Menu ========= */
const MENU: Readonly<MenuDef[]> = [
  {
    kind: "item",
    name: "Dashboard",
    icon: LayoutDashboard,
    allowedRoles: "ANY",
    path: "/dashboard",
  },
  {
    kind: "item",
    name: "User Management",
    icon: Shield,
    allowedRoles: ROUTE_ROLES.users,
    path: "/users",
  },
  {
    kind: "item",
    name: "Projects",
    icon: FolderKanban,
    allowedRoles: [
      "MADAR_ADMIN",
      "PROVIDER_ADMIN",
      "DATA_ANALYST",
      "SALES_REPRESENTATIVE",
      "FIELD_OPERATOR",
    ],
    path: "/projects",
  },

  {
    kind: "item",
    name: "Files",
    icon: FileArchive,
    allowedRoles: ROUTE_ROLES.files,
    path: "/files",
  },

  {
    kind: "item",
    name: "Comments",
    icon: MessageCircle,
    allowedRoles: ROUTE_ROLES.comments,
    path: "/comments",
  },
  {
    kind: "item",
    name: "Notifications",
    icon: Bell,
    allowedRoles: "ANY",
    path: "/notifications",
  },

  {
    kind: "item",
    name: "My Projects",
    icon: FolderKanban,
    allowedRoles: ROUTE_ROLES.myProjects,
    path: "/my-projects",
  },

  { kind: "label", label: "PERSONAL" },
  {
    kind: "item",
    name: "My Profile",
    icon: Users,
    allowedRoles: "ANY",
    path: "/profile",
  },
  {
    kind: "item",
    name: "Settings",
    icon: Settings,
    allowedRoles: ROUTE_ROLES.settings,
    path: "/settings",
  },
] as const;

type UseSidebarHookParams = Pick<
  SidebarProps,
  "activeMenuItem" | "setActiveMenuItem" | "onItemClick"
>;

export const useSidebarHook = ({
  activeMenuItem,
  setActiveMenuItem,
  onItemClick,
}: UseSidebarHookParams) => {
  /* ========= States ========= */
  const [hoveredItem, setHoveredItem] = useState<MenuName | null>(null);
  const [mounted, setMounted] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [hasInspectionEdits, setHasInspectionEdits] = useState(false);
  const [user, setUser] = useState<SidebarUser>(null);

  /* ========= Hooks ========= */
  const navigate = useNavigate();
  const location = useLocation();
  const apollo = useApolloClient();

  const [doLogout, { loading: loggingOut }] = useMutation(LOGOUT_MUTATION, {
    context: { fetchOptions: { credentials: "include" as const } },
  });

  /* ========= Derived ========= */
  const userRoles = user?.roles ?? [];
  const initial = user?.firstName?.[0]?.toUpperCase() ?? "U";

  const visibleItems = useMemo(() => {
    return MENU.filter(
      (item): item is MenuItemDef | MenuLabelDef =>
        item.kind === "label" ||
        (item.kind === "item" && hasAnyRole(userRoles, item.allowedRoles)),
    );
  }, [userRoles]);

  /* ========= Effects ========= */
  useEffect(() => {
    setCollapsed(getStoredCollapsed());
    setUser(getStoredUser());
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const items = visibleItems.filter(
      (item): item is MenuItemDef => item.kind === "item",
    );

    const pathname = location.pathname || "/";
    const matchedItem = getMatchedItemByPath(items, pathname);

    if (matchedItem) {
      setActiveMenuItem(matchedItem.name);
      return;
    }

    const names = items.map((item) => item.name);

    if (!names.includes(activeMenuItem) && names.length > 0) {
      setActiveMenuItem(names[0]);
    }
  }, [
    mounted,
    visibleItems,
    location.pathname,
    activeMenuItem,
    setActiveMenuItem,
  ]);

  useEffect(() => {
    const handleInspectionEditingChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { isEditing?: boolean }
        | undefined;

      if (typeof detail?.isEditing === "boolean") {
        setHasInspectionEdits(detail.isEditing);
      }
    };

    setHasInspectionEdits(getInitialInspectionEditState());

    window.addEventListener(
      "inspection:editing-changed",
      handleInspectionEditingChanged as EventListener,
    );

    return () => {
      window.removeEventListener(
        "inspection:editing-changed",
        handleInspectionEditingChanged as EventListener,
      );
    };
  }, []);

  /* ========= Handlers ========= */
  const handleMenuItemClick = (item: MenuItemDef) => {
    if (!mounted) return;

    if (item.path && hasInspectionEdits) {
      try {
        window.dispatchEvent(
          new CustomEvent("inspection:navigate", {
            detail: { navigateTo: item.path },
          }),
        );
      } catch {
        setActiveMenuItem(item.name);
        navigate(item.path);
      }
    } else {
      setActiveMenuItem(item.name);
      if (item.path) navigate(item.path);
    }

    onItemClick?.();
  };

  const handleLogout = async () => {
  try {
    try {
      (window as any).__INSPECTION_IS_EDITING = false;

      window.dispatchEvent(
        new CustomEvent("inspection:editing-changed", {
          detail: { isEditing: false },
        }),
      );
    } catch {
      //
    }

    await doLogout();
  } catch (error) {
    console.error("Logout failed:", error);
  } finally {
    try {
      await apollo.clearStore();
    } catch {
      //
    }

    const sidebarCollapsed = localStorage.getItem("sidebarCollapsed");

    localStorage.clear();

    if (sidebarCollapsed !== null) {
      localStorage.setItem("sidebarCollapsed", sidebarCollapsed);
    }

    window.dispatchEvent(new Event("auth:changed"));
    navigate("/login", { replace: true });
  }
};

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;

      localStorage.setItem("sidebarCollapsed", next ? "1" : "0");

      window.dispatchEvent(
        new CustomEvent("sidebar:collapsed", {
          detail: { collapsed: next },
        }),
      );

      return next;
    });
  };

  return {
    mounted,
    collapsed,
    hoveredItem,
    loggingOut,
    visibleItems,
    initial,
    user,
    setHoveredItem,
    handleMenuItemClick,
    handleLogout,
    toggleCollapsed,
  };
};
